import ConversationTransport from '../transport/ConversationTransport'
import { composeContext } from '../context/composeContext'
import { TurnError } from './types'
import type { GenerationTurnPlan, TurnOutcome, TurnStreamContext } from './types'
import type { StreamMeta } from '../transport/ConversationTransport'

interface RunGenerationTurnOptions {
  transport?: ConversationTransport
}

const errorMessage = (error: unknown, fallback: string): string =>
  error instanceof Error && error.message ? error.message : fallback

const asTurnError = (error: unknown, phase: TurnError['phase'], fallback: string): TurnError =>
  error instanceof TurnError ? error : new TurnError(phase, errorMessage(error, fallback), { cause: error })

/**
 * Runs one generation turn: optional preparation → context → stream →
 * completion and persistence.
 *
 * This is the single place stream callbacks are wired, so a capability added to
 * generation lands on every surface and every path at once. Everything
 * surface-specific arrives through the plan: where state is written
 * (`binding`), what becomes durable (`persistence`), what must happen before
 * generation (`hooks.prepare`), and how the request is routed (`request`).
 *
 * The pending timeline entry is created by the caller, not here — surfaces
 * create it alongside other optimistic state (roleplay shows the user message
 * and the assistant placeholder on the same frame). The runner drives that
 * entry from the moment the turn starts and removes it when the turn leaves
 * nothing worth showing.
 *
 * Failure semantics, which surfaces depend on:
 * - `prepare` throws       → TurnError('prepare'); the pending entry is removed.
 *                            Nothing generated, so no tokens were spent.
 * - context/stream throws  → TurnError('stream'); the entry is handed to
 *                            `binding.failed`, or removed if the surface has none.
 * - persistence throws     → TurnError('assistant-persist'); the entry is removed.
 *                            The content was never saved, so leaving it on screen
 *                            would promise durability the turn did not deliver.
 * - abort with no content  → the entry is removed and nothing is persisted,
 *                            unless the plan sets `dropEntryOnEmptyAbort: false`.
 * - abort with content     → the partial is kept and persisted.
 * - a mid-stream `error` event → same as a stream throw for the reader, but the
 *                            turn resolves with `ok: false` rather than throwing,
 *                            because the relay ends such streams without failing
 *                            the request.
 */
export async function runGenerationTurn<TInput>(
  plan: GenerationTurnPlan<TInput>,
  { transport = new ConversationTransport() }: RunGenerationTurnOptions = {},
): Promise<TurnOutcome> {
  const ctx: TurnStreamContext = { target: plan.target, streamId: plan.streamId }
  const isCancelled = plan.isCancelled ?? (() => false)

  const settle = async (outcome: TurnOutcome): Promise<TurnOutcome> => {
    await plan.hooks?.finalize?.(outcome)
    return outcome
  }

  /** Generation failed: let the surface show it, or drop the entry if it will not. */
  const failEntry = (error: Error): void => {
    if (plan.binding.failed) plan.binding.failed(ctx, error)
    else plan.binding.remove(ctx)
  }

  const cancelledOutcome = async (): Promise<TurnOutcome> => {
    plan.binding.remove(ctx)
    return settle({ ok: true, aborted: false, cancelled: true, content: '' })
  }

  if (plan.hooks?.prepare) {
    try {
      await plan.hooks.prepare()
    } catch (error: unknown) {
      // The pending entry never produced anything; drop it. Anything the hook
      // itself created is the hook's to roll back.
      plan.binding.remove(ctx)
      throw asTurnError(error, 'prepare', 'Failed to prepare the turn')
    }
  }

  if (isCancelled()) return cancelledOutcome()

  let finalContent = ''
  let aborted = false
  let persistError: unknown = null
  let streamError: Error | null = null

  try {
    // Context assembly is part of the stream phase: a failure here leaves
    // anything `prepare` made durable in place and drops only the pending entry.
    // The engine composes, so no surface can route a hand-built prompt around
    // its recipe.
    const messages = composeContext(plan.recipe, plan.buildInput())
    if (isCancelled()) return cancelledOutcome()

    const handle = await transport.generateStream(
      { ...plan.request, messages, streamId: plan.streamId },
      {
        onStart: () => plan.binding.begin(ctx),
        onConnected: () => {
          plan.binding.connected?.(ctx)
        },
        onReasoningDelta: (_delta, reasoning) => plan.binding.reasoning?.(ctx, reasoning),
        onContentDelta: (delta, content) => {
          plan.binding.content(ctx, delta, content)
        },
        onToolCallStart: (info) => plan.hooks?.onToolCallStart?.(info),
        onToolCall: async (event) => {
          await plan.hooks?.onToolCall?.(event)
        },
        onComplete: async ({ content, aborted: streamAborted, meta }: { content: string; aborted: boolean; meta?: StreamMeta }) => {
          finalContent = content
          aborted = streamAborted

          // A stop before any content leaves nothing worth keeping; a normal
          // stop keeps whatever streamed.
          if (streamAborted && !content.trim() && (plan.dropEntryOnEmptyAbort ?? true)) {
            plan.binding.remove(ctx)
            return
          }

          plan.binding.complete(ctx, { content, meta })

          // Persistence is awaited inside the stream so callers can rely on the
          // row existing once the turn resolves. The failure is captured rather
          // than thrown so it is not misread as a stream failure.
          try {
            await plan.persistence.persistAssistant(ctx, { content, aborted: streamAborted, meta })
          } catch (error: unknown) {
            // The write failed, so nothing durable exists. Drop the entry rather
            // than leave content on screen that a reload would not bring back.
            plan.binding.remove(ctx)
            persistError = error
          }
        },
        onError: (error: Error) => {
          streamError = error
          failEntry(error)
        },
      },
    )

    plan.onStreamHandle?.(handle)
    if (isCancelled()) handle.abort()
    await handle.promise
  } catch (error: unknown) {
    const turnError = asTurnError(error, 'stream', 'Failed to generate a reply')
    failEntry(turnError)
    await plan.hooks?.finalize?.({ ok: false, aborted, cancelled: false, content: finalContent })
    throw turnError
  }

  if (persistError) {
    const turnError = asTurnError(persistError, 'assistant-persist', 'Failed to save the reply')
    await plan.hooks?.finalize?.({ ok: false, aborted, cancelled: false, content: finalContent })
    throw turnError
  }

  return settle({
    ok: !streamError,
    aborted,
    cancelled: false,
    content: finalContent,
    ...(streamError ? { error: streamError } : {}),
  })
}

export default runGenerationTurn
